import {
    Bot,
    Mail,
    MessageCircle,
    MessageSquare,
    Phone,
    PhoneIncoming,
    PhoneOutgoing,
    StickyNote,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { InteractionDirection, InteractionType } from "../types";

const interactionConfig: Record<
    InteractionType,
    { icon: typeof Phone; label: string; color: string; bgColor: string }
> = {
    call: {
        icon: Phone,
        label: "Call",
        color: "text-green-600",
        bgColor: "bg-green-100",
    },
    sms: {
        icon: MessageSquare,
        label: "SMS",
        color: "text-blue-600",
        bgColor: "bg-blue-100",
    },
    chat: {
        icon: MessageCircle,
        label: "Chat",
        color: "text-purple-600",
        bgColor: "bg-purple-100",
    },
    email: {
        icon: Mail,
        label: "Email",
        color: "text-orange-600",
        bgColor: "bg-orange-100",
    },
    note: {
        icon: StickyNote,
        label: "Note",
        color: "text-gray-600",
        bgColor: "bg-gray-100",
    },
};

interface InteractionIconProps {
    type?: InteractionType;
    direction?: InteractionDirection;
    botId?: string;
    size?: "sm" | "md" | "lg";
    showLabel?: boolean;
    className?: string;
}

export const InteractionIcon = ({
    type = "note",
    direction,
    botId,
    size = "md",
    showLabel = false,
    className,
}: InteractionIconProps) => {
    const config = interactionConfig[type] || interactionConfig.note;

    // For calls, use direction-specific icons
    let IconComponent = config.icon;
    if (type === "call") {
        if (direction === "inbound") {
            IconComponent = PhoneIncoming;
        } else if (direction === "outbound") {
            IconComponent = PhoneOutgoing;
        }
    }

    const sizeClasses = {
        sm: "w-3 h-3",
        md: "w-4 h-4",
        lg: "w-5 h-5",
    };

    const containerSizeClasses = {
        sm: "p-1",
        md: "p-1.5",
        lg: "p-2",
    };

    return (
        <div
            className={cn(
                "inline-flex items-center gap-1.5",
                className
            )}
        >
            <span
                className={cn(
                    "rounded-full flex items-center justify-center",
                    containerSizeClasses[size],
                    config.bgColor
                )}
            >
                <IconComponent className={cn(sizeClasses[size], config.color)} />
            </span>
            {botId && (
                <Bot className={cn(sizeClasses[size], "text-muted-foreground")} />
            )}
            {showLabel && (
                <span className="text-xs text-muted-foreground">
                    {config.label}
                    {direction && ` (${direction})`}
                </span>
            )}
        </div>
    );
};

export const formatDuration = (seconds?: number): string => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
};

export const getInteractionLabel = (
    type?: InteractionType,
    direction?: InteractionDirection,
    botId?: string
): string => {
    const config = interactionConfig[type || "note"];
    let label = config.label;

    if (direction) {
        label = `${direction === "inbound" ? "Inbound" : "Outbound"} ${label.toLowerCase()}`;
    }

    if (botId) {
        label = `AI ${label}`;
    }

    return label;
};
